import Map from '../mapLayers/ClimateMap'
import { Layer, Source } from 'react-map-gl'
import ConfigProvider from 'balkerne-core/config'
import { Box, TextField, Grid, Typography, Tooltip, IconButton, Paper, InputAdornment } from '@mui/material'
import { useSnackbar } from 'notistack'
import { useMemo, useState, useCallback, useEffect, useRef } from 'react'
import type { ReactNode } from 'react'
import DrawControl from './DrawControl'
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined'
import { AllGeoJSON, bbox, centroid } from '@turf/turf'
import axios from 'axios'
import type { LayerProps } from 'react-map-gl'

const viewFillLayer: LayerProps = {
  id: 'drawmap-geometry-fill',
  type: 'fill',
  paint: {
    'fill-color': '#00e676',
    'fill-opacity': 0.18,
  },
}

const viewLineLayer: LayerProps = {
  id: 'drawmap-geometry-line',
  type: 'line',
  paint: {
    'line-color': '#00e676',
    'line-opacity': 0.9,
    'line-width': 2,
  },
}

type DrawMapProps = {
  onChange: (geometry: GeoJSON.FeatureCollection | null) => void
  onError: (error: string) => void
  getSearchValue?: () => string
  title: string
  initGeometry?: GeoJSON.FeatureCollection
  singlePolygon?: boolean
  editable?: boolean
  resetKey?: string | number
  headerActions?: ReactNode
  autoFitGeometry?: boolean
}

export const DrawMap = (props: DrawMapProps) => {
  const { onChange, onError, initGeometry, getSearchValue, title, singlePolygon, editable, resetKey, headerActions } =
    props
  const autoFitGeometry = props.autoFitGeometry ?? false
  const canEdit = editable ?? true
  const mapRef = useRef<any>()
  const postcodeRef = useRef<any>()

  const { enqueueSnackbar } = useSnackbar()
  const [features, setFeatures] = useState<Record<string, GeoJSON.Feature>>({})
  const [drawControlKey, setDrawControlKey] = useState(0)
  const [hasInitializedFeatures, setHasInitializedFeatures] = useState(false)
  const [mapLoaded, setMapLoaded] = useState(false)
  const lastFittedBboxKeyRef = useRef<string | null>(null)

  useEffect(() => {
    if (canEdit) return
    setFeatures({})
    setHasInitializedFeatures(false)
    lastFittedBboxKeyRef.current = null
  }, [canEdit])

  const initFeatures = useCallback((init: { [key: string]: GeoJSON.Feature }) => {
    setFeatures({ ...init })
    setHasInitializedFeatures(true)
  }, [])

  const featuresToFeatureCollection = useCallback((nextFeatures: Record<string, GeoJSON.Feature>) => {
    const list = Object.values(nextFeatures)
    if (list.length === 0) return null
    return {
      type: 'FeatureCollection',
      features: list.map((f: any) => {
        const { id: _id, ...rest } = f ?? {}
        return rest as GeoJSON.Feature
      }),
    } as GeoJSON.FeatureCollection
  }, [])

  const onUpdate = useCallback(
    (e: any) => {
      if (!canEdit) return
      setHasInitializedFeatures(true)
      setFeatures(currFeatures => {
        const next = { ...currFeatures }
        for (const f of e.features ?? []) {
          if (!f?.id) continue
          next[f.id] = f
        }
        return next
      })
    },
    [canEdit],
  )

  const onCreate = useCallback(
    (e: any) => {
      if (!canEdit) return
      setHasInitializedFeatures(true)
      const created = (e.features ?? []).find((f: any) => f?.id && f?.geometry?.type === 'Polygon')
      if (!created) return
      if (singlePolygon) {
        const next = { [created.id]: created }
        setFeatures(next)
        setDrawControlKey(k => k + 1)
        return
      }
      onUpdate(e)
    },
    [canEdit, onUpdate, singlePolygon],
  )

  const onDelete = useCallback(
    (e: any) => {
      if (!canEdit) return
      setHasInitializedFeatures(true)
      setFeatures(currFeatures => {
        const next = { ...currFeatures }
        for (const f of e.features ?? []) {
          if (!f?.id) continue
          delete next[f.id]
        }
        return next
      })
    },
    [canEdit],
  )

  const featureCollection = useMemo(
    () => featuresToFeatureCollection(features),
    [features, featuresToFeatureCollection],
  )
  useEffect(() => {
    if (!canEdit) return
    if (!hasInitializedFeatures) return
    onChange(featureCollection)
  }, [canEdit, featureCollection, hasInitializedFeatures, onChange])
  const renderedGeometry = useMemo(() => {
    if (!canEdit) return initGeometry ?? null
    return featureCollection ?? initGeometry ?? null
  }, [canEdit, featureCollection, initGeometry])

  useEffect(() => {
    if (!autoFitGeometry) return
    if (canEdit) return
    if (!mapLoaded) return
    if (!renderedGeometry || (renderedGeometry.features?.length ?? 0) === 0) return

    const map = mapRef.current?.getMap?.() ?? mapRef.current
    if (!map?.fitBounds) return

    try {
      const [minX, minY, maxX, maxY] = bbox(renderedGeometry as AllGeoJSON)
      const key = `${minX},${minY},${maxX},${maxY}`
      if (lastFittedBboxKeyRef.current === key) return
      lastFittedBboxKeyRef.current = key
      map.fitBounds(
        [
          [minX, minY],
          [maxX, maxY],
        ],
        { padding: 100, duration: 700, essential: true, maxZoom: 18 },
      )
    } catch (err) {
      console.warn(err)
    }
  }, [autoFitGeometry, canEdit, mapLoaded, renderedGeometry])

  const onSearchSuccess = (features: any) => {
    if (features.length > 0) {
      const { center } = features[0]
      mapRef?.current?.setCenter(center)
      mapRef?.current?.setZoom(17)
    } else {
      enqueueSnackbar('No matching addresses found', { variant: 'warning' })
    }
  }
  const onSearchError = (error: any) => {
    console.error(error)
    enqueueSnackbar('Error occured when searching for address.', { variant: 'error' })
    onError('Error occured when searching for address.')
  }
  const handleSearch = () => {
    const searchValue = getSearchValue ? getSearchValue() : postcodeRef?.current?.value
    searchLocation(searchValue, onSearchSuccess, onSearchError)
  }

  const initMap = () => {
    // Set initial view state to be over London
    const initialViewState = {
      latitude: 51.522,
      longitude: -0.226,
      zoom: 9,
    }
    const toCenter = renderedGeometry
    if (toCenter && toCenter.features?.length > 0) {
      const centerCoord = centroid(toCenter as AllGeoJSON).geometry.coordinates
      initialViewState.longitude = centerCoord[0]
      initialViewState.latitude = centerCoord[1]
      initialViewState.zoom = autoFitGeometry && !canEdit ? 12 : 14
    }
    return initialViewState
  }
  return (
    <div>
      <Paper elevation={0}>
        <Box>
          <Grid container spacing={1}>
            <Grid item xs>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                <Box>
                  <Typography variant="h6" component="h2">
                    {title}
                  </Typography>
                  {singlePolygon && canEdit && (
                    <Typography variant="caption" color="text.secondary">
                      Drawing a new polygon replaces the existing boundary.
                    </Typography>
                  )}
                </Box>
                {headerActions ? <Box sx={{ ml: 1 }}>{headerActions}</Box> : null}
              </Box>
            </Grid>
            {getSearchValue ? (
              <Grid item xs={2}>
                <Box sx={{ textAlign: 'right', marginBottom: 1 }}>
                  <Tooltip title="Search for an address">
                    <IconButton aria-label="Search" onClick={handleSearch}>
                      <SearchOutlinedIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Grid>
            ) : (
              <Grid item xs={12}>
                <TextField
                  sx={{ width: '100%', marginBottom: 1 }}
                  inputRef={postcodeRef}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="Search">
                          <IconButton aria-label="search" onClick={handleSearch} edge="end">
                            <SearchOutlinedIcon color={'action'} />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                  placeholder="Enter a postcode or address"
                />
              </Grid>
            )}
          </Grid>
          <Map
            ref={mapRef}
            mapStyle="mapbox://styles/mapbox/satellite-v9"
            initialViewState={initMap()}
            style={{ height: '600px' }}
            mapboxAccessToken={ConfigProvider.mapboxPublicKey}
            onLoad={() => setMapLoaded(true)}>
            {!canEdit && renderedGeometry && (
              <Source id="drawmap-geometry" type="geojson" data={renderedGeometry}>
                <Layer {...viewFillLayer} />
                <Layer {...viewLineLayer} />
              </Source>
            )}

            {canEdit && (
              <DrawControl
                key={`${drawControlKey}-${String(resetKey ?? 0)}`}
                position="top-left"
                displayControlsDefault={false}
                controls={{ polygon: true, trash: true }}
                defaultMode={(renderedGeometry?.features?.length ?? 0) > 0 ? 'simple_select' : 'draw_polygon'}
                onCreate={onCreate}
                onUpdate={onUpdate}
                onDelete={onDelete}
                initGeometry={renderedGeometry ?? undefined}
                initFeatures={initFeatures}
              />
            )}
          </Map>
        </Box>
      </Paper>
      <div style={{ marginLeft: 'auto', marginRight: 'auto', padding: 0 }}></div>
    </div>
  )
}

export const searchLocation = async (
  address: string,
  onSuccess: (features: any) => void,
  onError: (error: any) => void,
) => {
  try {
    const query = String(address ?? '').trim()
    if (!query) {
      onSuccess([])
      return
    }

    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json`
    const response = await axios.get(url, {
      params: {
        access_token: ConfigProvider.mapboxPublicKey,
        autocomplete: true,
        limit: 5,
      },
    })
    const { features } = response.data ?? {}
    onSuccess(features ?? [])
  } catch (err) {
    onError(err)
  }
}

export default DrawMap

DrawMap.defaultProps = {}
